import type { TFunction } from 'i18next';
import type { DataGridColumnProps } from '../../DataGrid/DataGrid';
import type { StatusPillColors } from '../../StatusPill/StatusPill';
import type { FilterValue } from '../filterDefinitions';

import { useTranslation } from 'react-i18next';

import { getTestProps } from '../../../lib/helpers';

import useDebounceCallback from '../../../hooks/useDebounceCallback';

import Dropdown from '../../Dropdown/Dropdown';
import Input from '../../Input/Input';
import StatusPill from '../../StatusPill/StatusPill';
import GridFilterValueDatasource from './GridFilterValueDatasource';

const getInputFieldType = (inputType?: string) => {
  switch (inputType) {
    case 'dateTime':
      return 'date';
    case 'number':
      return 'number';
    case 'text':
    default:
      return 'text';
  }
};

const getOptions = (t: TFunction, column?: DataGridColumnProps) => {
  switch (column?.filterInputType) {
    case 'checkbox':
      return [
        {
          label: t('Global.True'),
          value: 'true',
        },
        {
          label: t('Global.False'),
          value: 'false',
        },
      ];
    case 'internal.status':
      return (
        ['public', 'draft', 'modified', 'archived', 'scheduled'].map(
          (status) => {
            return {
              searchString: t(`ObjectStatus.Status.${status}`),
              value: status,
              label: (
                <StatusPill
                  status={t(`ObjectStatus.Status.${status}`)}
                  containerClasses="text-xs px-2.5 h-5 py-0 flex items-center"
                  color={status as StatusPillColors}
                />
              ),
            };
          },
        ) || []
      );
    default:
      return column?.filterInputOptions || [];
  }
};

interface GridFilterValueFieldProps {
  /**
   * Callback function that is called when the filter value changes.
   */
  onChange: (value: FilterValue) => void;
  /**
   * Current filter value.
   */
  value?: FilterValue;
  /**
   * The column definition for the field being filtered.
   */
  column?: DataGridColumnProps;
  /**
   * Test ID for the field.
   */
  testId?: string;
}

const GridFilterValueField = ({
  column,
  onChange,
  value,
  testId,
}: GridFilterValueFieldProps) => {
  const { t } = useTranslation();
  const inputType = column?.filterInputType;

  const handleChangeDebounced = useDebounceCallback(
    (value: FilterValue) => onChange?.(value),
    300,
  );

  switch (inputType) {
    case 'select':
    case 'radio':
    case 'checkbox':
    case 'internal.status':
    case 'internal.contentType':
      return (
        <Dropdown
          options={
            getOptions(t, column).map((el) =>
              typeof el === 'string' ? { value: el, label: el } : el,
            ) || []
          }
          value={value}
          placeholder={t('ObjectsOfType.FilterChooseValue')}
          variant="filter"
          onChange={(e) => onChange(e.target.value)}
          additionalInputClasses="px-2 max-w-34"
          additionalDropdownClasses="z-21 w-[calc(var(--button-width)+50px)]"
          anchor={{
            to: 'bottom end',
            gap: 12,
            padding: 16,
            offset: inputType === 'internal.contentType' ? 74 : 50,
          }}
          hideSearch={inputType !== 'internal.contentType'}
          isVirtualScrolling
          testId={testId}
        />
      );
    case 'datasource':
      return (
        <GridFilterValueDatasource
          column={column}
          onChange={onChange}
          value={String(value ?? '')}
          testId={testId}
        />
      );
    default:
      return (
        <Input
          type={getInputFieldType(column?.filterInputType)}
          value={value as string | number | undefined}
          additionalClasses="w-34"
          additionalInputClasses="h-6 w-34 px-2"
          placeholder={t('ObjectsOfType.FilterValuePlaceholder')}
          onChange={(e) => handleChangeDebounced(e.target.value)}
          {...getTestProps(testId)}
        />
      );
  }
};

export default GridFilterValueField;
